import axios from 'axios';
import api, { fetchCSRFToken, endSession } from './api';
import { useAuthStore } from '../store';

/**
 * Image Upload API Extensions
 * Handles file uploads for logos, backgrounds, and other media
 */

// Same base URL as every other API call (api.js). This used to fall back to
// http://<host>:5000/api whenever REACT_APP_API_URL wasn't baked into the
// build — and the production image is built without it — so every upload went
// to plain http on port 5000, which the admin site's Content-Security-Policy
// (connect-src 'self' https:) blocks.
const uploadAPI = axios.create({
    baseURL: api.defaults.baseURL,
    timeout: 60000, // Longer timeout for uploads
    withCredentials: true, // needed to send the x-csrf-token cookie back to the server
});

// Add auth token + a fresh CSRF token to every upload request. Uploads are
// infrequent (not a hot path like regular API calls), so there's no real
// cost to always fetching a fresh token here rather than caching one —
// which sidesteps the whole "cached token went stale" class of bug entirely
// for this axios instance (see api.js's response-interceptor fix for the
// general case, which this doesn't need since it never caches).
uploadAPI.interceptors.request.use(
    async (config) => {
        // The access token lives in the auth store's memory, as for api.js —
        // it hasn't been in localStorage for a long time, so uploads went out
        // without it and got 401 "No token provided".
        let token = useAuthStore.getState().token;
        if (!token && useAuthStore.getState().isAuthenticated) {
            // Just after a reload, before any other request refreshed it.
            token = await refreshAccessToken();
            if (!token) {
                endSession();
                return Promise.reject(new Error('Session expired'));
            }
        }
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        const csrfToken = await fetchCSRFToken();
        if (csrfToken) {
            config.headers['x-csrf-token'] = csrfToken;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

async function refreshAccessToken() {
    try {
        const res = await axios.post(`${api.defaults.baseURL}/auth/refresh`, {}, { withCredentials: true });
        const token = res.data?.data?.token || null;
        if (token) useAuthStore.getState().login(useAuthStore.getState().admin, token);
        return token;
    } catch {
        return null;
    }
}

// Token expired in flight: renew it once and retry the upload; when it can't
// be renewed, forget the old token and ask to log in again (as api.js does).
uploadAPI.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        if (error.response?.status !== 401 || !original || original._retry) return Promise.reject(error);
        original._retry = true;
        const token = await refreshAccessToken();
        if (!token) {
            endSession();
            return Promise.reject(error);
        }
        original.headers.Authorization = `Bearer ${token}`;
        return uploadAPI(original);
    },
);

/**
 * Tenant Image Uploads
 */
export const tenantUploadAPI = {
    uploadLogo: (id, file) => {
        const formData = new FormData();
        formData.append('logo', file);
        return uploadAPI.post(`/tenants/${id}/logo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    uploadFavicon: (id, file) => {
        const formData = new FormData();
        formData.append('favicon', file);
        return uploadAPI.post(`/tenants/${id}/favicon`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    uploadBackground: (id, file) => {
        const formData = new FormData();
        formData.append('background', file);
        return uploadAPI.post(`/tenants/${id}/background`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    updateBranding: (id, brandingData) => {
        return uploadAPI.put(`/tenants/${id}/branding`, brandingData);
    },
};

/**
 * Branch Image Uploads
 */
export const branchUploadAPI = {
    uploadLogo: (id, file) => {
        const formData = new FormData();
        formData.append('logo', file);
        return uploadAPI.post(`/branches/${id}/logo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    uploadBackground: (id, file) => {
        const formData = new FormData();
        formData.append('background', file);
        return uploadAPI.post(`/branches/${id}/background`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    updateBranding: (id, brandingData) => {
        return uploadAPI.put(`/branches/${id}/branding`, brandingData);
    },
};

/**
 * NAS Portal Image Uploads
 */
export const nasUploadAPI = {
    uploadPortalLogo: (id, file) => {
        const formData = new FormData();
        formData.append('logo', file);
        return uploadAPI.post(`/nas/${id}/portal-logo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    uploadPortalBackground: (id, file) => {
        const formData = new FormData();
        formData.append('background', file);
        return uploadAPI.post(`/nas/${id}/portal-background`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    uploadLogo: (id, file) => {
        const formData = new FormData();
        formData.append('logo', file);
        return uploadAPI.post(`/nas/${id}/portal-logo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    uploadBackground: (id, file) => {
        const formData = new FormData();
        formData.append('background', file);
        return uploadAPI.post(`/nas/${id}/portal-background`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    updatePortalConfig: (id, portalConfig) => {
        return uploadAPI.put(`/nas/${id}/portal-config`, portalConfig);
    },
};

/**
 * Template Design Image Uploads — key is one of:
 * 'logo' | 'backgroundImage' | 'partnerBanner' | 'footerStrip'
 */
export const templateUploadAPI = {
    uploadDesignImage: (id, key, file) => {
        const formData = new FormData();
        formData.append('image', file);
        return uploadAPI.post(`/templates/${id}/design-image/${key}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

/**
 * Helper function to create file preview URL
 */
export const createFilePreview = (file) => {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error('No file provided'));
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

/**
 * Validate image file
 */
export const validateImageFile = (file, maxSizeMB = 5) => {
    // Matches the backend's actual allowlist (middlewares/upload.js) — SVG is
    // deliberately excluded there (untrusted SVG needs real sanitization this
    // project doesn't have yet), so don't let it pass client-side only to
    // fail with a confusing error once it reaches the server.
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    if (!file) {
        return { valid: false, error: 'No file selected' };
    }

    if (!allowedTypes.includes(file.type)) {
        return {
            valid: false,
            error: 'Invalid file type. Only JPEG, PNG, GIF, and WEBP are allowed.'
        };
    }

    const maxSize = maxSizeMB * 1024 * 1024; // Convert to bytes
    if (file.size > maxSize) {
        return {
            valid: false,
            error: `File size must be less than ${maxSizeMB}MB`
        };
    }

    return { valid: true };
};

export default {
    tenantUploadAPI,
    branchUploadAPI,
    nasUploadAPI,
    templateUploadAPI,
    createFilePreview,
    validateImageFile,
};
